import React, { useRef, useState, useEffect } from 'react';
import { Bold, Italic, X } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import Button from 'components/Button';
import type {
  HeaderData,
  HeaderLogoAlignment,
  HeaderNavItem,
} from 'types/websiteBuilder';
import { compressImageFilePng } from 'utils/imageUtils';
import { ROUTES } from 'utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import logoPlaceholder from 'assets/websiteBuilderDefaults/logo-placeholder.svg';
import { BUILDER_STORAGE_KEY } from '../builderTemplates';
import FontColorPicker from './FontColorPicker';
import { FONT_FAMILY_OPTIONS } from './fontFamilyOptions';
import styles from './HeaderSection.module.scss';

const LOGO_ALIGNMENTS: HeaderLogoAlignment[] = ['left', 'right', 'center'];

const slugifyMenuLabel = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

const getSlugFromHref = (href?: string): string => {
  const raw = (href ?? '').trim();
  if (!raw) return '';
  const withoutFragment = raw.split('#')[0].split('?')[0];
  const wwwMatch = withoutFragment.match(/^\/?www\/[^/]+\/([^/]+)$/i);
  if (wwwMatch) return wwwMatch[1].replace(/^\/+/, '').toLowerCase();
  return withoutFragment.replace(/^\/+/, '').toLowerCase();
};

const isDonateNavItem = (item: HeaderNavItem): boolean => {
  const slug = getSlugFromHref(item.href);
  const label = (item.label ?? '').trim().toLowerCase();
  return slug === 'donate' || label === 'donate';
};

function getData(data: Record<string, unknown>): HeaderData {
  const hasNavItemsArray = Array.isArray(data.navItems);
  const navItems = hasNavItemsArray
    ? (data.navItems as Record<string, unknown>[]).map((i) => ({
        label: typeof i.label === 'string' ? i.label : '',
        href: typeof i.href === 'string' ? i.href : '/',
        visible: typeof i.visible === 'boolean' ? i.visible : true,
        sourcePageId:
          typeof i.sourcePageId === 'string' ? i.sourcePageId : undefined,
      }))
    : [];

  const alignment = data.logoAlignment as HeaderLogoAlignment | undefined;
  const logoAlignment =
    alignment && LOGO_ALIGNMENTS.includes(alignment) ? alignment : 'center';

  const rawSize =
    (data.logoSizePx as number) || (data.logoSizeInches as number);
  const logoSizePx =
    typeof rawSize === 'number' && Number.isFinite(rawSize) ? rawSize : 55;

  const rawOffsetY = data.logoOffsetY;
  const logoOffsetY =
    typeof rawOffsetY === 'number' && Number.isFinite(rawOffsetY)
      ? rawOffsetY
      : 0;

  const rawLogoTextFontSize = data.logoTextFontSize;
  const logoTextFontSize =
    typeof rawLogoTextFontSize === 'number' && rawLogoTextFontSize > 0
      ? rawLogoTextFontSize
      : 16;
  const rawLogoTextColor = data.logoTextColor;

  const rawNavFontSize = data.navFontSize;
  const navFontSize =
    typeof rawNavFontSize === 'number' && rawNavFontSize > 0
      ? rawNavFontSize
      : 16;
  const rawNavTextColor = data.navTextColor;

  return {
    logoText:
      typeof data.logoText === 'string' && data.logoText.trim()
        ? data.logoText
        : DEFAULT_HEADER_DATA.logoText,
    logoImageUrl:
      typeof data.logoImageUrl === 'string' ? data.logoImageUrl : '',
    logoTextFontFamily:
      typeof data.logoTextFontFamily === 'string'
        ? data.logoTextFontFamily
        : "'Trebuchet MS', 'Segoe UI', sans-serif",
    logoTextBold:
      typeof data.logoTextBold === 'boolean' ? data.logoTextBold : false,
    logoTextItalic:
      typeof data.logoTextItalic === 'boolean' ? data.logoTextItalic : false,
    logoTextFontSize,
    logoTextColor: typeof rawLogoTextColor === 'string' ? rawLogoTextColor : '',
    logoAlignment,
    logoSizePx,
    logoOffsetY,
    navItems: hasNavItemsArray
      ? navItems
      : [
          { label: 'Home', href: '/home' },
          { label: 'About Us', href: '/about-us' },
          { label: 'Visit Us', href: '/visit-us' },
        ],
    navFontFamily:
      typeof data.navFontFamily === 'string'
        ? data.navFontFamily
        : "'Lato', Arial, sans-serif",
    navFontSize,
    navTextColor: typeof rawNavTextColor === 'string' ? rawNavTextColor : '',
    navBold: typeof data.navBold === 'boolean' ? data.navBold : false,
    navItalic: typeof data.navItalic === 'boolean' ? data.navItalic : false,
    backgroundOverlay:
      typeof data.backgroundOverlay === 'boolean'
        ? data.backgroundOverlay
        : false,
  };
}

export interface HeaderSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

const DEFAULT_HEADER_DATA = {
  logoText: 'My Site Title',
};

export function HeaderSectionEditor({
  data,
  onUpdateData,
}: HeaderSectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    index: number;
    label: string;
  } | null>(null);
  const [lastDeleted, setLastDeleted] = useState<{
    item: HeaderNavItem;
    index: number;
  } | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const logoOffsetY = d.logoOffsetY ?? 0;
  const alignment = d.logoAlignment ?? 'center';
  const logoSizePx = d.logoSizePx ?? 55;
  const logoSrc = d.logoImageUrl || logoPlaceholder;
  const logoTextFontFamily = d.logoTextFontFamily ?? '';
  const logoTextFontSize = d.logoTextFontSize ?? 16;
  const logoTextColor = d.logoTextColor ?? '';
  const navFontFamily = d.navFontFamily ?? '';
  const navFontSize = d.navFontSize ?? 16;
  const navTextColor = d.navTextColor ?? '';
  const backgroundOverlay = d.backgroundOverlay ?? false;

  const update = (patch: Partial<HeaderData>) => {
    onUpdateData({ ...data, ...patch });
  };

  const updateNavItem = (
    index: number,
    field: keyof HeaderNavItem,
    value: string,
  ) => {
    const items = [...(d.navItems ?? [])];
    if (!items[index]) items[index] = { label: '', href: '/' };
    if (field === 'label') {
      const slug = slugifyMenuLabel(value);
      items[index] = {
        ...items[index],
        label: value,
        href: slug ? `/${slug}` : '',
      };
    } else {
      items[index] = { ...items[index], [field]: value };
    }
    update({ navItems: items });
  };

  const addNavItem = () => {
    const sourcePageId = `page-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const existingItems = [...(d.navItems ?? [])];
    const donateIndex = existingItems.findIndex((item) =>
      isDonateNavItem(item),
    );
    const newItem: HeaderNavItem = { label: '', href: '', sourcePageId };
    if (donateIndex >= 0) {
      existingItems.splice(donateIndex, 0, newItem);
      update({ navItems: existingItems });
      return;
    }
    update({
      navItems: [...existingItems, newItem],
    });
  };

  const removeNavItem = (index: number) => {
    const items = (d.navItems ?? []).filter((_, i) => i !== index);
    update({ navItems: items });
  };

  const confirmRemoveNavItem = (index: number) => {
    const item = d.navItems?.[index];
    if (!item) return;
    setDeleteTarget({
      index,
      label: item.label?.trim() || 'menu item',
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    const item = d.navItems?.[deleteTarget.index];
    if (!item) {
      setDeleteTarget(null);
      return;
    }
    setLastDeleted({ item, index: deleteTarget.index });
    removeNavItem(deleteTarget.index);
    setDeleteTarget(null);
  };

  const handleUndoDelete = () => {
    if (!lastDeleted) return;
    const next = [...(d.navItems ?? [])];
    const insertIndex = Math.max(0, Math.min(lastDeleted.index, next.length));
    next.splice(insertIndex, 0, lastDeleted.item);
    update({ navItems: next });
    setLastDeleted(null);
  };

  const updateNavItemVisibility = (index: number, visible: boolean) => {
    const items = [...(d.navItems ?? [])];
    if (!items[index]) return;
    items[index] = { ...items[index], visible };
    update({ navItems: items });
  };

  const handleDragStart =
    (index: number) => (e: React.DragEvent<HTMLButtonElement>) => {
      setDragIndex(index);
      setDragOverIndex(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    };

  const handleDragOver =
    (index: number) => (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverIndex !== index) setDragOverIndex(index);
    };

  const handleDrop =
    (index: number) => (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const fallbackFrom = Number.parseInt(
        e.dataTransfer.getData('text/plain'),
        10,
      );
      const fromIndex =
        dragIndex ?? (Number.isFinite(fallbackFrom) ? fallbackFrom : -1);
      if (fromIndex < 0 || fromIndex === index) {
        setDragIndex(null);
        setDragOverIndex(null);
        return;
      }

      const current = [...(d.navItems ?? [])];
      if (fromIndex >= current.length || index >= current.length) {
        setDragIndex(null);
        setDragOverIndex(null);
        return;
      }

      const [moved] = current.splice(fromIndex, 1);
      current.splice(index, 0, moved);
      update({ navItems: current });
      setDragIndex(null);
      setDragOverIndex(null);
    };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    compressImageFilePng(file)
      .then((imageUrl) => update({ logoImageUrl: imageUrl }))
      .catch(() => {});
    e.target.value = '';
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleLogoFileChange}
        aria-label="Upload logo"
      />
      <label
        className={styles.giveVisibilityRow}
        style={{ alignSelf: 'flex-start' }}
      >
        <input
          type="checkbox"
          checked={backgroundOverlay}
          onChange={(e) => update({ backgroundOverlay: e.target.checked })}
        />
        Overlay header background
      </label>
      <div className={styles.logoInputs}>
        <div
          className={styles.logoPreview}
          style={{ width: '60px', height: '60px' }}
        >
          <img src={logoSrc} alt="" />
        </div>
        <div className={styles.logoControlLabel}>
          <input
            type="text"
            className={styles.logoUrlInput}
            value={d.logoImageUrl ?? ''}
            onChange={(e) => update({ logoImageUrl: e.target.value })}
            placeholder="Logo image URL (optional)"
          />
        </div>
        <div className={styles.logoActionRow}>
          <button
            type="button"
            className={styles.browseBtn}
            onClick={() => fileInputRef.current?.click()}
          >
            Browse
          </button>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => update({ logoImageUrl: '' })}
          >
            Clear
          </button>
        </div>
        <div className={styles.logoControls}>
          <div className={styles.logoControlRow}>
            <label className={styles.logoControlLabel} htmlFor="logo-alignment">
              Logo alignment:
            </label>
            <select
              id="logo-alignment"
              className={styles.logoControlSelect}
              value={alignment}
              onChange={(e) =>
                update({
                  logoAlignment: e.target.value as HeaderLogoAlignment,
                })
              }
            >
              {LOGO_ALIGNMENTS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.logoControlRow}>
            <label className={styles.logoControlLabel} htmlFor="logo-size">
              Logo size:
            </label>
            <input
              id="logo-size"
              type="number"
              className={styles.logoControlInput}
              value={logoSizePx}
              step={1}
              onChange={(e) =>
                update({
                  logoSizePx: Number.parseInt(e.target.value || '0', 10),
                })
              }
            />
          </div>
          <div className={styles.logoControlRow}>
            <label className={styles.logoControlLabel} htmlFor="logo-offset-y">
              Logo vertical offset:
            </label>
            <input
              id="logo-offset-y"
              type="number"
              className={styles.logoControlInput}
              value={logoOffsetY}
              step={1}
              onChange={(e) =>
                update({
                  logoOffsetY: Number.parseInt(e.target.value || '0', 10),
                })
              }
            />
          </div>
        </div>
        <div className={styles.fontGroup}>
          <div className={styles.fontGroupTitle}>Site Title:</div>
          <div className={styles.toolbarRow}>
            <FontColorPicker
              value={logoTextColor}
              onChange={(color) => update({ logoTextColor: color })}
              compact
            />
            <button
              type="button"
              className={`${styles.toolbarBtn} ${d.logoTextBold ? styles.toolbarBtnActive : ''}`}
              onClick={() => update({ logoTextBold: !d.logoTextBold })}
              aria-label="Toggle site title bold"
            >
              <Bold size={12} />
            </button>
            <button
              type="button"
              className={`${styles.toolbarBtn} ${d.logoTextItalic ? styles.toolbarBtnActive : ''}`}
              onClick={() => update({ logoTextItalic: !d.logoTextItalic })}
              aria-label="Toggle site title italic"
            >
              <Italic size={12} />
            </button>
          </div>
          <textarea
            className={styles.logoTextInput}
            rows={2}
            value={(data.logoText as string) || ''}
            onChange={(e) => update({ logoText: e.target.value })}
            placeholder={DEFAULT_HEADER_DATA.logoText}
          />
          <div className={styles.fontGroupRow}>
            <label className={styles.fontLabel}>
              Font:
              <select
                className={styles.fontSelect}
                value={logoTextFontFamily}
                onChange={(e) => update({ logoTextFontFamily: e.target.value })}
              >
                {FONT_FAMILY_OPTIONS.map((option) => (
                  <option key={`logo-${option.label}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.fontLabel}>
              Size:
              <select
                className={styles.fontSelect}
                value={String(logoTextFontSize)}
                onChange={(e) =>
                  update({
                    logoTextFontSize: Number.parseInt(e.target.value, 10),
                  })
                }
              >
                {[14, 16, 18, 20, 22, 24, 28, 32].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Menu Items:</div>
        <div className={styles.toolbarRow}>
          <FontColorPicker
            value={navTextColor}
            onChange={(color) => update({ navTextColor: color })}
            compact
          />
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.navBold ? styles.toolbarBtnActive : ''}`}
            onClick={() => update({ navBold: !d.navBold })}
            aria-label="Toggle menu items bold"
          >
            <Bold size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.navItalic ? styles.toolbarBtnActive : ''}`}
            onClick={() => update({ navItalic: !d.navItalic })}
            aria-label="Toggle menu items italic"
          >
            <Italic size={12} />
          </button>
        </div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Font:
            <select
              className={styles.fontSelect}
              value={navFontFamily}
              onChange={(e) => update({ navFontFamily: e.target.value })}
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={`nav-${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fontLabel}>
            Size:
            <select
              className={styles.fontSelect}
              value={String(navFontSize)}
              onChange={(e) =>
                update({ navFontSize: Number.parseInt(e.target.value, 10) })
              }
            >
              {[12, 13, 14, 15, 16, 18, 20].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <div className={styles.navEdit}>
        {(d.navItems ?? []).map((item, index) => (
          <div
            key={item.sourcePageId ? `page-${item.sourcePageId}` : index}
            className={`${styles.navItemRow} ${
              dragIndex === index ? styles.navItemRowDragging : ''
            } ${dragOverIndex === index && dragIndex !== index ? styles.navItemRowDragOver : ''}`}
            onDragOver={handleDragOver(index)}
            onDrop={handleDrop(index)}
          >
            <button
              type="button"
              className={styles.dragHandle}
              draggable
              onDragStart={handleDragStart(index)}
              onDragEnd={handleDragEnd}
              aria-label="Drag to reorder menu item"
              title="Drag to reorder"
            >
              ⋮⋮
            </button>
            <input
              type="text"
              value={item.label ?? ''}
              onChange={(e) => updateNavItem(index, 'label', e.target.value)}
              placeholder={item.sourcePageId ? 'Linked page title' : 'Name'}
            />
            <input
              type="text"
              value={item.href ?? ''}
              onChange={(e) => updateNavItem(index, 'href', e.target.value)}
              placeholder={
                item.sourcePageId
                  ? '/www/<company-domain>/<slug>'
                  : 'Slug (ex. /about-us)'
              }
            />
            {isDonateNavItem(item) ? (
              <label className={styles.rowToggle} title="Show or hide Donate">
                <input
                  type="checkbox"
                  checked={item.visible !== false}
                  onChange={(e) =>
                    updateNavItemVisibility(index, e.target.checked)
                  }
                />
                <span>Show</span>
              </label>
            ) : (
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => confirmRemoveNavItem(index)}
                aria-label="Remove menu item"
                title="Remove menu item"
              >
                ×
              </button>
            )}
          </div>
        ))}
        <div className={styles.navActionRow}>
          <button type="button" className={styles.addBtn} onClick={addNavItem}>
            Add menu item
          </button>
          <button
            type="button"
            className={styles.addBtn}
            onClick={handleUndoDelete}
            disabled={!lastDeleted}
            title="Undo last deleted menu item and destination page"
          >
            Undo delete
          </button>
        </div>
      </div>
      <Modal
        show={deleteTarget != null}
        onHide={() => setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete menu item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete "<strong>{deleteTarget?.label ?? 'menu item'}</strong>" and its
          destination page?
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

interface HeaderSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function HeaderSection({ data, onUpdateData }: HeaderSectionProps) {
  const d = getData(data);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isInWebsiteBuilder = location.pathname.startsWith(
    ROUTES.MEMBERSHIP.WEBSITE_BUILDER,
  );

  const getPublicSlugFromHref = (href?: string): string | null => {
    const raw = (href ?? '').trim();
    if (!raw) return null;
    const withoutFragment = raw.split('#')[0].split('?')[0];

    // /www/<company-domain>/<slug>
    const wwwMatch = withoutFragment.match(/^\/?www\/[^/]+\/([^/]+)$/i);
    if (wwwMatch) {
      return wwwMatch[1].replace(/^\/+/, '');
    }

    // /<slug>
    if (withoutFragment.startsWith('/')) {
      return withoutFragment.replace(/^\/+/, '');
    }

    return withoutFragment;
  };

  const getCompanyDomainFromStorage = (): string => {
    try {
      const raw = localStorage.getItem(BUILDER_STORAGE_KEY);
      if (!raw) return '';
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== 'object' || parsed == null) return '';
      const domain = (parsed as { companyDomain?: unknown }).companyDomain;
      if (typeof domain !== 'string') return '';
      return domain.trim().toLowerCase();
    } catch {
      return '';
    }
  };

  const buildPublicHref = (slug: string): string => {
    const normalizedSlug = slug.trim().replace(/^\/+/, '').toLowerCase();
    const domain = getCompanyDomainFromStorage();
    if (!domain) return `/${normalizedSlug}`;
    return `/www/${domain}/${normalizedSlug}`;
  };

  const getNavHref = (item: HeaderNavItem): string => {
    const slug = getPublicSlugFromHref(item.href);
    if (!slug) return '/';
    return buildPublicHref(slug);
  };

  const normalizeSlug = (s: string): string =>
    s.trim().replace(/^\/+/, '').toLowerCase();

  const handleSignUpClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    navigate(ROUTES.MEMBERSHIP.ROOT);
  };

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: HeaderNavItem,
  ) => {
    setIsMobileMenuOpen(false);
    const slug = getPublicSlugFromHref(item.href);
    if (!slug) return;

    const normalizedSlug = normalizeSlug(slug);

    if (isInWebsiteBuilder) {
      e.preventDefault();

      // Special-case: Home should never create a new page.
      if (normalizedSlug === 'home') {
        navigate(ROUTES.MEMBERSHIP.WEBSITE_BUILDER);
        return;
      }

      if (typeof item.sourcePageId === 'string' && item.sourcePageId.trim()) {
        navigate(
          ROUTES.MEMBERSHIP.WEBSITE_BUILDER_PAGE.replace(
            ':pageId',
            item.sourcePageId,
          ),
        );
        return;
      }

      // Create a new destination page by linking the clicked nav item.
      // `WebsiteBuilder.tsx` will detect the new `sourcePageId` and auto-create
      // the corresponding `WebsiteBuilderPage`.
      const navItems = d.navItems ?? [];
      const navItemIndex = navItems.indexOf(item);
      if (navItemIndex < 0) {
        navigate(ROUTES.MEMBERSHIP.WEBSITE_BUILDER);
        return;
      }

      const newPageId = `page-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      const nextNavItems = navItems.map((ni, idx) =>
        idx === navItemIndex
          ? {
              ...ni,
              sourcePageId: newPageId,
              href: buildPublicHref(normalizedSlug),
            }
          : ni,
      );
      onUpdateData({ navItems: nextNavItems });

      navigate(
        ROUTES.MEMBERSHIP.WEBSITE_BUILDER_PAGE.replace(':pageId', newPageId),
      );
      return;
    }

    // Public site navigation.
    e.preventDefault();
    const rawHref = (item.href ?? '').trim();
    if (/^\/?www\/[^/]+\/[^/]+$/i.test(rawHref)) {
      navigate(rawHref.startsWith('/') ? rawHref : `/${rawHref}`);
      return;
    }
    navigate(buildPublicHref(normalizedSlug));
  };
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const alignment = d.logoAlignment ?? 'center';
  const isCenter = alignment === 'center';
  const visibleItems = (d.navItems ?? []).filter(
    (item) => item.visible !== false,
  );
  const items = visibleItems.filter((item) => !isDonateNavItem(item));
  const donateItem = visibleItems.find((item) => isDonateNavItem(item)) ?? null;
  // Keep left side greater/equal when centered (5 -> 3|2, 6 -> 3|3).
  const half = Math.ceil(items.length / 2);
  const leftItems = isCenter ? items.slice(0, half) : items;
  const rightItems = isCenter ? items.slice(half) : [];
  const logoSizePx = d.logoSizePx ?? 55;
  const logoOffsetY = d.logoOffsetY ?? 0;
  const logoTextFontFamily = d.logoTextFontFamily?.trim() || '';
  const logoTextFontSize = d.logoTextFontSize ?? 16;
  const logoTextBold = d.logoTextBold ?? false;
  const logoTextItalic = d.logoTextItalic ?? false;
  const navFontFamily = d.navFontFamily?.trim() || '';
  const navFontSize = d.navFontSize ?? 16;
  const navBold = d.navBold ?? false;
  const navItalic = d.navItalic ?? false;
  const backgroundOverlay = d.backgroundOverlay ?? false;

  const headerStyle: React.CSSProperties | undefined = backgroundOverlay
    ? {
        background: isScrolled ? 'hsl(0 0% 8% / 0.72)' : 'hsl(0 0% 12% / 0.72)',
      }
    : undefined;

  const holderStyle: React.CSSProperties = {
    width: logoSizePx,
    height: logoSizePx,
    minWidth: logoSizePx,
    transform: `translateY(${logoOffsetY}px)`,
  };

  const renderLogoAndTitle = (isRight = false) => (
    <div
      className={`${styles.logoArea} ${isRight ? styles.logoAreaReverse : ''}`}
    >
      <div className={styles.logoHolder} style={holderStyle}>
        <img
          src={d.logoImageUrl || logoPlaceholder}
          alt=""
          className={styles.logoImage}
        />
      </div>
      <span
        className={styles.logoText}
        style={{
          fontSize: `${logoTextFontSize}px`,
          fontWeight: logoTextBold ? 700 : 400,
          fontStyle: logoTextItalic ? 'italic' : 'normal',
          ...(d.logoTextColor ? { color: d.logoTextColor } : {}),
          ...(logoTextFontFamily ? { fontFamily: logoTextFontFamily } : {}),
        }}
      >
        {d.logoText?.trim() ? d.logoText : 'Site title'}
      </span>
    </div>
  );

  const navList = (
    <ul className={styles.navList}>
      {items.map((item, index) => (
        <li key={item.sourcePageId ? `page-${item.sourcePageId}` : index}>
          <a
            href={getNavHref(item)}
            onClick={(e) => handleNavClick(e, item)}
            className={isDonateNavItem(item) ? styles.navDonateLink : undefined}
            style={{
              fontSize: `${navFontSize}px`,
              fontWeight: navBold ? 700 : 400,
              fontStyle: navItalic ? 'italic' : 'normal',
              ...(d.navTextColor ? { color: d.navTextColor } : {}),
              ...(navFontFamily ? { fontFamily: navFontFamily } : {}),
            }}
          >
            {item.label || 'Link'}
          </a>
        </li>
      ))}
    </ul>
  );

  const donateNavLink = donateItem ? (
    <a
      href={getNavHref(donateItem)}
      onClick={(e) => handleNavClick(e, donateItem)}
      className={styles.navDonateLink}
      style={{
        fontSize: `${navFontSize}px`,
        fontWeight: navBold ? 700 : 400,
        fontStyle: navItalic ? 'italic' : 'normal',
        ...(d.navTextColor ? { color: d.navTextColor } : {}),
        ...(navFontFamily ? { fontFamily: navFontFamily } : {}),
      }}
    >
      {donateItem.label || 'Donate'}
    </a>
  ) : null;

  const mobileNavDrawer = isMobileMenuOpen ? (
    <div
      className={styles.mobileDrawerBackdrop}
      onClick={() => setIsMobileMenuOpen(false)}
      role="button"
      tabIndex={0}
      aria-label="Close mobile menu"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsMobileMenuOpen(false);
        }
      }}
    >
      <aside
        className={styles.mobileDrawer}
        onClick={(e) => e.stopPropagation()}
        aria-label="Mobile navigation menu"
      >
        <div className={styles.mobileDrawerTopBar}>
          <span className={styles.mobileDrawerTitle}>Menu</span>
          <button
            type="button"
            className={styles.mobileDrawerClose}
            aria-label="Close menu"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X size={22} strokeWidth={2.25} aria-hidden />
          </button>
        </div>
        <nav className={styles.mobileDrawerNav} aria-label="Site pages">
          <ul className={styles.mobileDrawerList}>
            {items.map((item, index) => (
              <li
                key={
                  item.sourcePageId ? `mobile-page-${item.sourcePageId}` : index
                }
                className={styles.mobileDrawerListItem}
              >
                <a
                  href={getNavHref(item)}
                  onClick={(e) => handleNavClick(e, item)}
                  className={styles.mobileDrawerLink}
                  style={{
                    fontWeight: navBold ? 700 : 500,
                    fontStyle: navItalic ? 'italic' : 'normal',
                    ...(navFontFamily ? { fontFamily: navFontFamily } : {}),
                  }}
                >
                  {item.label || 'Link'}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className={styles.mobileDrawerActions}>
          <a
            href={ROUTES.MEMBERSHIP.ROOT}
            className={styles.mobileSignUpBtn}
            onClick={handleSignUpClick}
          >
            Sign up
          </a>
          {donateItem ? (
            <a
              href={getNavHref(donateItem)}
              onClick={(e) => handleNavClick(e, donateItem)}
              className={styles.navDonateLink}
              style={{
                fontSize: `${navFontSize}px`,
                fontWeight: navBold ? 700 : 400,
                fontStyle: navItalic ? 'italic' : 'normal',
                ...(navFontFamily ? { fontFamily: navFontFamily } : {}),
              }}
            >
              {donateItem.label || 'Donate'}
            </a>
          ) : null}
        </div>
      </aside>
    </div>
  ) : null;

  return (
    <header
      className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}
      style={headerStyle}
    >
      <div
        className={`${styles.inner} ${styles[`inner${alignment.charAt(0).toUpperCase()}${alignment.slice(1)}`]}`}
      >
        {alignment === 'left' && (
          <>
            {renderLogoAndTitle(false)}
            <nav className={styles.nav}>
              <div className={styles.navRowWithDonate}>
                {navList}
                {donateNavLink}
              </div>
            </nav>
          </>
        )}
        {alignment === 'right' && (
          <>
            <nav className={styles.nav}>{navList}</nav>
            {renderLogoAndTitle(true)}
            {donateNavLink}
          </>
        )}
        {alignment === 'center' && (
          <>
            <nav className={styles.navLeft}>
              <ul className={styles.navList}>
                {leftItems.map((item, index) => (
                  <li
                    key={
                      item.sourcePageId
                        ? `left-page-${item.sourcePageId}`
                        : index
                    }
                  >
                    <a
                      href={getNavHref(item)}
                      onClick={(e) => handleNavClick(e, item)}
                      className={
                        isDonateNavItem(item) ? styles.navDonateLink : undefined
                      }
                      style={{
                        fontSize: `${navFontSize}px`,
                        fontWeight: navBold ? 700 : 400,
                        fontStyle: navItalic ? 'italic' : 'normal',
                        ...(d.navTextColor ? { color: d.navTextColor } : {}),
                        ...(navFontFamily ? { fontFamily: navFontFamily } : {}),
                      }}
                    >
                      {item.label || 'Link'}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div className={styles.logoCenter}>{renderLogoAndTitle(false)}</div>
            <nav className={styles.navRight}>
              <div className={styles.navRowWithDonate}>
                <ul className={styles.navList}>
                  {rightItems.map((item, index) => (
                    <li
                      key={
                        item.sourcePageId
                          ? `right-plain-page-${item.sourcePageId}`
                          : index
                      }
                    >
                      <a
                        href={getNavHref(item)}
                        onClick={(e) => handleNavClick(e, item)}
                        style={{
                          fontSize: `${navFontSize}px`,
                          fontWeight: navBold ? 700 : 400,
                          fontStyle: navItalic ? 'italic' : 'normal',
                          ...(d.navTextColor ? { color: d.navTextColor } : {}),
                          ...(navFontFamily
                            ? { fontFamily: navFontFamily }
                            : {}),
                        }}
                      >
                        {item.label || 'Link'}
                      </a>
                    </li>
                  ))}
                </ul>
                {donateNavLink}
              </div>
            </nav>
          </>
        )}
        <button
          type="button"
          className={styles.mobileMenuToggle}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
        >
          <span className={styles.mobileMenuBar} />
          <span className={styles.mobileMenuBar} />
          <span className={styles.mobileMenuBar} />
        </button>
      </div>
      {mobileNavDrawer}
    </header>
  );
}

export default HeaderSection;
